import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, redirectExternal } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, Loader2, Sparkles, Zap, Settings, Coins } from "lucide-react";

const FREE_FEATURES = [
  "Fino a 3 siti web",
  "30 generazioni AI ogni 10 minuti",
  "Editor visuale completo",
  "Pubblicazione con URL personalizzato",
];

const PRO_FEATURES = [
  "Siti web illimitati",
  "200 generazioni AI ogni 10 minuti",
  "Tutto quello che c'è nel piano Free",
  "Supporto prioritario",
];

export default function Upgrade() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);

  useEffect(() => {
    refreshUser();
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPro = user?.plan === "pro";
  const price = plans?.pro_price;
  const priceLabel = price ? `€${Number(price.amount).toFixed(0)}` : "€9";
  const creditPacks = plans?.credit_packs || [];

  const buyPack = async (lookupKey) => {
    setBuyingPack(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, lookup_key: lookupKey });
      redirectExternal(data.checkout_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBuyingPack(null);
    }
  };

  const upgrade = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      redirectExternal(data.checkout_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      redirectExternal(data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-white p-1" data-testid="upgrade-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-display font-bold text-lg">Piani & Prezzi</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-center max-w-xl mx-auto mb-12">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-5">
            <Sparkles className="w-7 h-7" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">
            Crea senza limiti
          </h1>
          <p className="text-muted-foreground mt-4">
            Passa a Angiò A.I. Pro e sblocca siti illimitati e più generazioni AI per far crescere la tua presenza online.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8" data-testid="plan-free">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-xl">Free</h2>
              {!isPro && (
                <span className="text-[10px] font-mono text-success border border-success/30 bg-success/10 rounded-full px-2 py-0.5">
                  ATTUALE
                </span>
              )}
            </div>
            <div className="mt-4 flex items-end gap-1">
              <span className="text-4xl font-display font-bold">€0</span>
              <span className="text-muted-foreground mb-1">/mese</span>
            </div>
            <ul className="mt-7 space-y-3">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-white/40 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="relative rounded-2xl border border-primary/40 bg-gradient-to-b from-primary/[0.08] to-transparent p-8 shadow-[0_0_40px_-12px_rgba(0,240,255,0.3)]" data-testid="plan-pro">
            <div className="absolute -top-3 left-8 text-[10px] font-mono text-black bg-primary rounded-full px-3 py-1 font-bold tracking-wide">
              CONSIGLIATO
            </div>
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-xl flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" /> Pro
              </h2>
              {isPro && (
                <span className="text-[10px] font-mono text-primary border border-primary/40 bg-primary/10 rounded-full px-2 py-0.5">
                  ATTIVO
                </span>
              )}
            </div>
            <div className="mt-4 flex items-end gap-1">
              <span className="text-4xl font-display font-bold">{priceLabel}</span>
              <span className="text-muted-foreground mb-1">/mese</span>
            </div>
            <ul className="mt-7 space-y-3">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {isPro ? (
              <Button onClick={openPortal} disabled={portalLoading}
                className="w-full mt-8 bg-white/10 text-white hover:bg-white/15 font-semibold h-11" data-testid="manage-subscription-btn">
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings className="w-4 h-4 mr-2" /> Gestisci abbonamento</>}
              </Button>
            ) : (
              <Button onClick={upgrade} disabled={loading}
                className="w-full mt-8 bg-primary text-black hover:bg-[#33F3FF] font-semibold h-11 transition-transform hover:-translate-y-0.5" data-testid="upgrade-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Passa a Pro</>}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Pagamento sicuro gestito da Stripe. Puoi annullare quando vuoi.
        </p>

        {/* AI credit packs (one-time) */}
        <div className="mt-20 max-w-3xl mx-auto" data-testid="credit-packs-section">
          <div className="text-center mb-8">
            <div className="w-12 h-12 mx-auto rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary mb-4">
              <Coins className="w-6 h-6" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold">Pacchetti di crediti AI</h2>
            <p className="text-muted-foreground mt-2 text-sm max-w-md mx-auto">
              Niente abbonamento? Acquista generazioni AI extra una tantum. I crediti si usano
              quando finisci quelle del tuo piano e non scadono.
            </p>
            {plans && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="current-credits">
                Crediti attuali: <span className="text-primary font-mono font-semibold">{plans.ai_credits ?? 0}</span>
              </p>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {creditPacks.map((pack) => (
              <div key={pack.lookup_key} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-center justify-between gap-4" data-testid={`credit-pack-${pack.lookup_key}`}>
                <div>
                  <div className="font-display font-semibold text-lg flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" /> {pack.credits} generazioni
                  </div>
                  <div className="text-2xl font-display font-bold mt-2">€{Number(pack.amount).toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    €{(pack.amount / pack.credits).toFixed(3)} / generazione
                  </div>
                </div>
                <Button onClick={() => buyPack(pack.lookup_key)} disabled={buyingPack === pack.lookup_key}
                  className="bg-primary text-black hover:bg-[#33F3FF] font-semibold h-11 px-5 shrink-0" data-testid={`buy-pack-${pack.lookup_key}`}>
                  {buyingPack === pack.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Acquista"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
